/**
 * Widget styles as a template literal.
 *
 * All styles are injected into Shadow DOM, ensuring complete
 * isolation from the host page. CSS custom properties (--hana-*)
 * are set at the :host level from entity branding config.
 */

export function getWidgetStyles(): string {
  return /* css */ `
    :host {
      /* Branding tokens — set programmatically from config */
      --hana-primary: #1565C0;
      --hana-primary-hover: #1565C0;
      --hana-secondary: #42A5F5;
      --hana-bg: #FFFFFF;
      --hana-surface: #F5F5F5;
      --hana-text: #212121;
      --hana-text-muted: #757575;
      --hana-border: #E0E0E0;
      --hana-bubble-bot-bg: #F5F5F5;
      --hana-suggested-bg: #F5F5F5;
      --hana-suggested-border: #E0E0E0;
      --hana-button-primary-bg: var(--hana-primary);
      --hana-button-primary-hover-bg: var(--hana-primary-hover);
      --hana-button-outline-color: var(--hana-primary);
      --hana-cta-radius: 8px;
      --hana-font: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      --hana-button-radius: 12px;

      /* Typography tokens (7-tier scale per FRD Section 8.3) */
      --hana-heading-lg-size: 24px;
      --hana-heading-lg-weight: 700;
      --hana-heading-size: 16px;
      --hana-heading-weight: 600;
      --hana-body-size: 14px;
      --hana-body-weight: 400;
      --hana-body-md-size: 14px;
      --hana-body-md-weight: 500;
      --hana-body-sm-size: 12px;
      --hana-body-sm-weight: 400;
      --hana-caption-size: 12px;
      --hana-caption-weight: 400;
      --hana-label-sm-size: 11px;
      --hana-label-sm-weight: 500;

      /* Layout tokens */
      --hana-panel-width: 400px;
      --hana-header-height: 56px;
      --hana-input-height: 64px;
      --hana-toggle-size: 56px;
      --hana-toggle-margin: 20px;

      /* REQ-D-046 shell-variant layout tokens.
         Defaults preserve the Wave-1 Concierge Panel footprint when
         no embedder customisation is provided. */
      --hana-panel-width-compact: 320px;
      --hana-panel-width-expanded: 480px;
      --hana-panel-height-compact: 440px;
      --hana-panel-height-expanded: 640px;
      --hana-modal-width: 600px;
      --hana-modal-height: 720px;
      --hana-modal-backdrop-opacity: 0.4;
      --hana-header-height-compact: 48px;
      --hana-header-height-expanded: 64px;
      --hana-size-transition: 200ms cubic-bezier(0.4, 0.0, 0.2, 1);
      /* Per-axis launcher offset (override either axis from host style to
         move the floating toggle without affecting the other axis). Falls
         back to --hana-toggle-margin so existing embeds continue to work. */
      --hana-toggle-bottom: var(--hana-toggle-margin);
      --hana-toggle-right: var(--hana-toggle-margin);
      --hana-toggle-left: var(--hana-toggle-margin);
      --hana-border-radius: 12px;
      --hana-z-index: 10000;

      /* Animation */
      --hana-transition-speed: 250ms;
      --hana-transition-easing: cubic-bezier(0.4, 0, 0.2, 1);

      all: initial;
      font-family: var(--hana-font);
      font-size: var(--hana-body-size);
      font-weight: var(--hana-body-weight);
      line-height: 1.5;
      color: var(--hana-text);
      box-sizing: border-box;
    }

    *, *::before, *::after {
      box-sizing: border-box;
    }

    /* ===== TOGGLE BUTTON ===== */

    .hana-toggle {
      position: fixed;
      bottom: var(--hana-toggle-bottom);
      right: var(--hana-toggle-right);
      width: var(--hana-toggle-size);
      height: var(--hana-toggle-size);
      border-radius: 50%;
      background: var(--hana-primary);
      color: #FFFFFF;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: var(--hana-z-index);
      transition: transform var(--hana-transition-speed) var(--hana-transition-easing),
                  box-shadow var(--hana-transition-speed) var(--hana-transition-easing);
      padding: 0;
      outline: none;
    }

    .hana-toggle:hover {
      transform: scale(1.05);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    }

    .hana-toggle:focus-visible {
      outline: 2px solid var(--hana-primary);
      outline-offset: 2px;
    }

    .hana-toggle--left {
      right: auto;
      left: var(--hana-toggle-left);
    }

    .hana-toggle--hidden {
      transform: scale(0);
      pointer-events: none;
    }

    .hana-toggle__icon {
      width: 28px;
      height: 28px;
      fill: currentColor;
    }

    /* Custom launcher image (branding.launcherIconUrl / data-launcher-icon).
       The image fills the button edge-to-edge and the brand-colour disc is
       suppressed, so the supplied mark IS the launcher. Reverts to the
       default glyph + primary colour if the image is absent or fails. */
    .hana-toggle--custom-icon {
      background: transparent;
    }

    .hana-toggle__icon--replaced {
      display: none;
    }

    .hana-toggle__image {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      object-fit: cover;
      display: block;
      pointer-events: none;
    }

    .hana-toggle__image--hidden {
      display: none;
    }

    .hana-toggle__badge {
      position: absolute;
      top: -4px;
      right: -4px;
      min-width: 20px;
      height: 20px;
      border-radius: 10px;
      background: #D32F2F;
      color: #FFFFFF;
      font-size: 11px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 5px;
    }

    .hana-toggle__badge--hidden {
      display: none;
    }

    /* ===== CHAT PANEL ===== */

    .hana-panel {
      position: fixed;
      bottom: 0;
      right: 0;
      width: var(--hana-panel-width);
      height: 100vh;
      max-height: 100vh;
      background: var(--hana-bg);
      z-index: calc(var(--hana-z-index) + 1);
      display: flex;
      flex-direction: column;
      box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
      transform: translateX(100%);
      transition: transform var(--hana-transition-speed) var(--hana-transition-easing);
      overflow: hidden;
    }

    .hana-panel--left {
      right: auto;
      left: 0;
      box-shadow: 4px 0 24px rgba(0, 0, 0, 0.12);
      transform: translateX(-100%);
    }

    .hana-panel--open {
      transform: translateX(0);
    }

    /* ===== HEADER ===== */

    .hana-header {
      display: flex;
      align-items: center;
      height: var(--hana-header-height);
      min-height: var(--hana-header-height);
      padding: 0 16px;
      background: var(--hana-primary);
      color: #FFFFFF;
      gap: 12px;
    }

    .hana-header__logo {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      object-fit: cover;
      flex-shrink: 0;
    }

    .hana-header__logo--hidden {
      display: none;
    }

    .hana-header__title {
      flex: 1;
      font-size: var(--hana-heading-size);
      font-weight: var(--hana-heading-weight);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .hana-header__close {
      width: 32px;
      height: 32px;
      border: none;
      background: transparent;
      color: #FFFFFF;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      flex-shrink: 0;
      padding: 0;
      outline: none;
      transition: background var(--hana-transition-speed) var(--hana-transition-easing);
    }

    .hana-header__close:hover {
      background: rgba(255, 255, 255, 0.15);
    }

    .hana-header__close:focus-visible {
      outline: 2px solid #FFFFFF;
      outline-offset: -2px;
    }

    .hana-header__close-icon {
      width: 20px;
      height: 20px;
      fill: currentColor;
    }

    /* ===== MESSAGE LIST ===== */

    .hana-messages {
      flex: 1;
      overflow-y: auto;
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      scroll-behavior: smooth;
      overscroll-behavior: contain;
    }

    .hana-messages__welcome {
      padding: 12px 16px;
      background: var(--hana-surface);
      border-radius: var(--hana-border-radius);
      color: var(--hana-text);
      font-size: var(--hana-body-size);
      font-weight: var(--hana-body-weight);
      line-height: 1.5;
      white-space: pre-line;
    }

    .hana-messages__empty {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      color: var(--hana-text-muted);
      font-size: var(--hana-caption-size);
    }

    /* GH #792 — WhatsApp transition notice */
    .hana-messages__transition-separator {
      height: 1px;
      background: var(--hana-border);
      margin: 16px 0 8px;
    }

    .hana-messages__transition-notice {
      background: #e8f5e9;
      border-left: 3px solid #4caf50;
      border-radius: var(--hana-border-radius);
      padding: 12px 16px;
      margin: 8px 16px 16px;
      color: var(--hana-text);
      font-size: var(--hana-caption-size);
      line-height: 1.5;
      text-align: center;
    }

    /* ===== INPUT AREA ===== */

    .hana-input {
      display: flex;
      align-items: flex-end;
      padding: 12px 16px;
      border-top: 1px solid var(--hana-border);
      gap: 8px;
      background: var(--hana-bg);
      min-height: var(--hana-input-height);
    }

    .hana-input__field {
      flex: 1;
      border: 1px solid var(--hana-border);
      border-radius: 20px;
      padding: 8px 16px;
      font-family: var(--hana-font);
      font-size: var(--hana-body-size);
      line-height: 1.4;
      color: var(--hana-text);
      background: var(--hana-bg);
      resize: none;
      outline: none;
      max-height: 120px;
      overflow-y: auto;
      transition: border-color var(--hana-transition-speed) var(--hana-transition-easing);
    }

    .hana-input__field::placeholder {
      color: var(--hana-text-muted);
    }

    .hana-input__field:focus {
      border-color: var(--hana-primary);
    }

    .hana-input__send {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      border: none;
      background: var(--hana-primary);
      color: #FFFFFF;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      padding: 0;
      outline: none;
      transition: background var(--hana-transition-speed) var(--hana-transition-easing),
                  opacity var(--hana-transition-speed) var(--hana-transition-easing);
    }

    .hana-input__send:hover {
      background: var(--hana-primary-hover);
    }

    .hana-input__send:focus-visible {
      outline: 2px solid var(--hana-primary);
      outline-offset: 2px;
    }

    .hana-input__send:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .hana-input__send-icon {
      width: 18px;
      height: 18px;
      fill: currentColor;
    }

    /* ===== CONSENT PROMPT ===== */

    .hana-consent {
      background: #F8F9FA;
      border: 1px solid var(--hana-border);
      border-left: 4px solid var(--hana-primary);
      border-radius: var(--hana-border-radius);
      padding: 16px;
      margin: 4px 0;
    }

    .hana-consent__text {
      font-size: var(--hana-caption-size);
      line-height: 1.6;
      color: var(--hana-text);
      margin: 0 0 12px 0;
    }

    .hana-consent__tou-link {
      color: var(--hana-primary-hover, var(--hana-primary));
      text-decoration: underline;
      font-weight: 500;
      cursor: pointer;
    }

    .hana-consent__tou-link:hover {
      text-decoration: none;
    }

    .hana-consent__tou-link:focus-visible {
      outline: 2px solid var(--hana-primary);
      outline-offset: 2px;
      border-radius: 2px;
    }

    .hana-consent__actions {
      display: flex;
      gap: 8px;
      margin-top: 12px;
    }

    .hana-consent__btn {
      padding: 8px 20px;
      border-radius: var(--hana-cta-radius);
      font-family: var(--hana-font);
      font-size: var(--hana-caption-size);
      font-weight: 600;
      cursor: pointer;
      border: none;
      outline: none;
      transition: background var(--hana-transition-speed) var(--hana-transition-easing),
                  opacity var(--hana-transition-speed) var(--hana-transition-easing);
    }

    .hana-consent__btn:focus-visible {
      outline: 2px solid var(--hana-primary);
      outline-offset: 2px;
    }

    .hana-consent__btn--agree {
      background: var(--hana-primary);
      color: #FFFFFF;
    }

    .hana-consent__btn--agree:hover {
      background: var(--hana-primary-hover);
    }

    .hana-consent__btn--decline {
      background: transparent;
      color: var(--hana-text-muted);
      border: 1px solid var(--hana-border);
    }

    .hana-consent__btn--decline:hover {
      background: var(--hana-button-primary-hover-bg);
      border-color: var(--hana-button-primary-hover-bg);
      color: #FFFFFF;
    }

    .hana-consent__btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .hana-consent__status {
      font-size: var(--hana-caption-size);
      line-height: 1.5;
      padding: 8px 12px;
      border-radius: 8px;
      margin-top: 8px;
    }

    .hana-consent__status--granted {
      background: #E8F5E9;
      color: #2E7D32;
    }

    .hana-consent__status--declined {
      background: #FFF3E0;
      color: #E65100;
    }

    /* ===== ACTION BUTTONS (suggested actions / recommended) =====
       Per EEC Figma (PDF p. 56-61): pill quick-replies use radius 20px,
       min-height 36px, and either a filled "primary" variant
       (recommended action — orange fill, white text) or an outlined
       "secondary" variant (alternate replies — orange border, orange
       text). The base .hana-action-btn ships as secondary; pills
       marked with .hana-action-btn--primary (sparkle + recommended)
       flip to the filled treatment. Focus-ring is 2px solid at 2px
       offset so it stays visible against the surrounding bubble. */

    .hana-action-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      min-height: 36px;
      padding: 8px 16px;
      border-radius: 20px;
      border: 1px solid var(--hana-button-outline-color);
      background: transparent;
      color: var(--hana-button-outline-color);
      font-family: var(--hana-font);
      font-size: var(--hana-body-size);
      font-weight: 500;
      line-height: 21px;
      cursor: pointer;
      outline: none;
      transition: background var(--hana-transition-speed) var(--hana-transition-easing),
                  color var(--hana-transition-speed) var(--hana-transition-easing),
                  box-shadow var(--hana-transition-speed) var(--hana-transition-easing);
    }

    .hana-action-btn:hover {
      background: var(--hana-button-primary-hover-bg);
      border-color: var(--hana-button-primary-hover-bg);
      color: #FFFFFF;
    }

    .hana-action-btn:focus-visible {
      outline: 2px solid var(--hana-primary);
      outline-offset: 2px;
    }

    /* Filled "primary" pill — used for the recommended action (sparkle)
       in a suggestions row. Distinct visual weight signals the bot's
       pick vs the alternate replies. */
    .hana-action-btn--primary {
      background: var(--hana-button-primary-bg);
      color: #ffffff;
      border-color: var(--hana-button-primary-bg);
    }
    .hana-action-btn--primary:hover {
      background: var(--hana-button-primary-hover-bg);
      border-color: var(--hana-button-primary-hover-bg);
    }

    /* ===== SUGGESTED ACTIONS CONTAINER ===== */

    /* Default: horizontal pill row — legacy Wave-1 look. */
    .hana-suggested-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      padding: 4px 0;
    }

    .hana-suggested-actions:empty {
      display: none;
      padding: 0;
      margin: 0;
    }

    /* REQ-D-046 — static empty-state starter pills. Dedicated class
       (NOT .hana-suggested-actions, so empty-state and runtime pills
       stay independently selectable). Same horizontal wrap layout +
       .hana-action-btn token so the look matches the runtime pills
       per entity branding. */
    .hana-suggested-prompts {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      padding: 4px 0;
      margin-top: 4px;
    }

    .hana-suggested-prompts:empty {
      display: none;
      padding: 0;
      margin: 0;
    }

    /* List variant — stacked rounded rows per the EEC Figma's doctor /
       clinic picker. Used when the backend sets display:"list" OR when
       actions.length > 4 (auto-promotion is the widget's call, see
       SuggestedActions.renderActions). Each row is full-width, radius
       12px, left-aligned text, lighter border treatment. */
    .hana-suggested-actions--list {
      flex-direction: column;
      flex-wrap: nowrap;
      gap: 6px;
      align-items: stretch;
    }
    .hana-suggested-actions--list .hana-action-btn {
      width: 100%;
      max-width: none;
      justify-content: flex-start;
      text-align: left;
      border-radius: 12px;
      border-color: var(--hana-border);
      color: var(--hana-button-outline-color);
      padding: 10px 14px;
      min-height: 44px;
    }
    .hana-suggested-actions--list .hana-action-btn:hover {
      background: var(--hana-button-primary-hover-bg);
      color: #FFFFFF;
      border-color: var(--hana-button-primary-hover-bg);
    }
    .hana-suggested-actions--list .hana-suggested-pill {
      max-width: none;
    }
    .hana-suggested-actions--list .hana-suggested-pill__label {
      white-space: normal;
    }

    /* GH #560 (S4b) — doctor-selection pills (specialist discovery).
       Two-line branded card per the HMI mock: doctor name strong on
       the first line, the admin's ≤4-word capability tag muted
       beneath. Always rendered in the stacked list layout (the
       component forces --list for doctor groups); 44px+ touch target
       for the mobile-first EEC widget. Tag-only invariant: this
       surface NEVER renders paragraphs (plan §4.2). */
    .hana-doctor-pill {
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
      padding: 10px 14px;
      min-height: 52px;
    }
    .hana-doctor-pill__name {
      font-weight: 600;
      font-size: var(--hana-body-size);
      line-height: 1.35;
      color: inherit;
    }
    .hana-doctor-pill__tag {
      font-size: var(--hana-caption-size);
      font-weight: 500;
      line-height: 1.3;
      color: var(--hana-text-muted);
    }
    .hana-doctor-pill:hover .hana-doctor-pill__tag,
    .hana-doctor-pill:focus-visible .hana-doctor-pill__tag {
      color: inherit;
    }

    /* Detail-card action set ("See available slots" primary +
       "Back to doctor list" secondary) keeps the stacked layout but
       lets the primary affordance keep its filled treatment inside
       the --list variant (the generic --list row styling would
       otherwise flatten it to an outline). */
    .hana-suggested-actions--detail .hana-action-btn--primary {
      background: var(--hana-button-primary-bg);
      color: #ffffff;
      border-color: var(--hana-button-primary-bg);
      justify-content: center;
      text-align: center;
    }
    .hana-suggested-actions--detail .hana-action-btn--primary:hover {
      background: var(--hana-button-primary-hover-bg);
      border-color: var(--hana-button-primary-hover-bg);
    }

    @keyframes hana-pill-enter {
      from {
        opacity: 0;
        transform: translateY(8px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .hana-suggested-actions--entering .hana-suggested-pill {
      animation: hana-pill-enter var(--hana-transition-speed) var(--hana-transition-easing) both;
    }

    .hana-suggested-actions--entering .hana-suggested-pill:nth-child(1) {
      animation-delay: 0ms;
    }
    .hana-suggested-actions--entering .hana-suggested-pill:nth-child(2) {
      animation-delay: 50ms;
    }
    .hana-suggested-actions--entering .hana-suggested-pill:nth-child(3) {
      animation-delay: 100ms;
    }
    .hana-suggested-actions--entering .hana-suggested-pill:nth-child(4) {
      animation-delay: 150ms;
    }
    .hana-suggested-actions--entering .hana-suggested-pill:nth-child(5) {
      animation-delay: 200ms;
    }

    .hana-suggested-pill {
      max-width: 200px;
      background: var(--hana-suggested-bg);
      border-color: var(--hana-suggested-border);
      color: var(--hana-button-outline-color);
      font-size: var(--hana-caption-size);
      line-height: 18px;
      box-shadow:
        0 1px 1px rgba(0, 0, 0, 0.10),
        0 1px 1.5px rgba(0, 0, 0, 0.08);
    }

    .hana-suggested-pill__label {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .hana-suggested-pill__ext-icon {
      display: inline-flex;
      align-items: center;
      margin-left: 4px;
      flex-shrink: 0;
    }

    /* ===== SPARKLE ANIMATION (EEC recommended actions) =====
       Single-cycle shimmer that draws the eye to the bot's recommended
       pick (sparkle: true). Gated on prefers-reduced-motion below so
       motion-sensitive users see a steady highlight, not a sweep. */

    @keyframes hana-sparkle {
      0% {
        background-position: -200% center;
      }
      100% {
        background-position: 200% center;
      }
    }

    .hana-sparkle {
      position: relative;
      overflow: hidden;
    }

    .hana-sparkle::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(255, 255, 255, 0.3) 50%,
        transparent 100%
      );
      background-size: 200% 100%;
      animation: hana-sparkle 2s ease-in-out 0.5s 1;
      pointer-events: none;
      border-radius: inherit;
    }

    /* Reduced-motion fallback — skip the sweep, keep a soft highlight
       so the recommended pick still reads as "highlighted". */
    @media (prefers-reduced-motion: reduce) {
      .hana-sparkle::after {
        animation: none;
        background: rgba(255, 255, 255, 0.12);
      }
    }

    /* ===== FEEDBACK CAPTURE ===== */

    .hana-feedback {
      background: var(--hana-surface);
      border: 1px solid var(--hana-border);
      border-left: 4px solid var(--hana-primary);
      border-radius: var(--hana-border-radius);
      padding: 16px;
      margin: 4px 0;
    }

    .hana-feedback__prompt {
      font-size: var(--hana-body-size);
      font-weight: 500;
      color: var(--hana-text);
      margin: 0 0 12px 0;
      line-height: 1.5;
    }

    .hana-feedback__prompt:empty {
      display: none;
      margin: 0;
    }

    .hana-feedback__content {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .hana-feedback__binary {
      display: flex;
      gap: 12px;
    }

    .hana-feedback__thumb {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      border: 1px solid var(--hana-border);
      background: var(--hana-bg);
      color: var(--hana-text-muted);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      outline: none;
      transition: background var(--hana-transition-speed) var(--hana-transition-easing),
                  color var(--hana-transition-speed) var(--hana-transition-easing),
                  border-color var(--hana-transition-speed) var(--hana-transition-easing);
    }

    .hana-feedback__thumb:hover {
      border-color: var(--hana-primary);
      color: var(--hana-primary-hover, var(--hana-primary));
      background: var(--hana-surface);
    }

    .hana-feedback__thumb:focus-visible {
      outline: 2px solid var(--hana-primary);
      outline-offset: 2px;
    }

    .hana-feedback__thumb-icon {
      width: 22px;
      height: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .hana-feedback__thumb-icon svg {
      width: 22px;
      height: 22px;
      fill: currentColor;
    }

    .hana-feedback__stars {
      display: flex;
      gap: 4px;
    }

    .hana-feedback__star {
      width: 36px;
      height: 36px;
      border: none;
      background: transparent;
      color: var(--hana-text-muted);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      outline: none;
      transition: color var(--hana-transition-speed) var(--hana-transition-easing),
                  transform var(--hana-transition-speed) var(--hana-transition-easing);
    }

    .hana-feedback__star:hover {
      transform: scale(1.15);
    }

    .hana-feedback__star:focus-visible {
      outline: 2px solid var(--hana-primary);
      outline-offset: 2px;
      border-radius: 4px;
    }

    .hana-feedback__star-icon {
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .hana-feedback__star-icon svg {
      width: 24px;
      height: 24px;
      fill: var(--hana-primary);
    }

    .hana-feedback__skip {
      align-self: flex-start;
      padding: 4px 12px;
      border: none;
      background: transparent;
      color: var(--hana-text-muted);
      font-family: var(--hana-font);
      font-size: var(--hana-caption-size);
      cursor: pointer;
      outline: none;
      transition: color var(--hana-transition-speed) var(--hana-transition-easing);
    }

    .hana-feedback__skip:hover {
      color: var(--hana-text);
      text-decoration: underline;
    }

    .hana-feedback__skip:focus-visible {
      outline: 2px solid var(--hana-primary);
      outline-offset: 2px;
      border-radius: 2px;
    }

    .hana-feedback__confirmation {
      font-size: var(--hana-caption-size);
      line-height: 1.5;
      padding: 8px 12px;
      border-radius: 8px;
      background: #E8F5E9;
      color: #2E7D32;
    }

    .hana-feedback__dismissed {
      font-size: var(--hana-caption-size);
      line-height: 1.5;
      padding: 8px 12px;
      border-radius: 8px;
      background: var(--hana-surface);
      color: var(--hana-text-muted);
    }

    /* ===== INLINE FORM ===== */

    .hana-form {
      background: var(--hana-surface);
      border: 1px solid var(--hana-border);
      border-left: 4px solid var(--hana-primary);
      border-radius: var(--hana-border-radius);
      padding: 16px;
      margin: 4px 0;
    }

    .hana-form__title {
      font-size: var(--hana-heading-size);
      font-weight: var(--hana-heading-weight);
      color: var(--hana-text);
      margin: 0 0 12px 0;
      line-height: 1.4;
    }

    .hana-form__fields {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .hana-form-field {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .hana-form-field__label {
      font-size: var(--hana-caption-size);
      font-weight: 500;
      color: var(--hana-text);
      line-height: 1.4;
    }

    .hana-form-field__required {
      color: #D32F2F;
      font-weight: 600;
    }

    .hana-form-field__input {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid var(--hana-border);
      border-radius: 8px;
      font-family: var(--hana-font);
      font-size: var(--hana-body-size);
      color: var(--hana-text);
      background: var(--hana-bg);
      outline: none;
      transition: border-color var(--hana-transition-speed) var(--hana-transition-easing);
    }

    .hana-form-field__input:focus {
      border-color: var(--hana-primary);
    }

    .hana-form-field__input::placeholder {
      color: var(--hana-text-muted);
    }

    .hana-form-field__input--error {
      border-color: #D32F2F;
    }

    .hana-form-field__input--error:focus {
      border-color: #D32F2F;
    }

    .hana-form-field__input:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      background: var(--hana-surface);
    }

    /* Phone field */

    .hana-form-field__phone-wrapper {
      display: flex;
      align-items: center;
      gap: 0;
      border: 1px solid var(--hana-border);
      border-radius: 8px;
      background: var(--hana-bg);
      overflow: hidden;
      transition: border-color var(--hana-transition-speed) var(--hana-transition-easing);
    }

    .hana-form-field__phone-wrapper:focus-within {
      border-color: var(--hana-primary);
    }

    .hana-form-field__phone-prefix {
      padding: 8px 10px;
      font-size: var(--hana-body-size);
      font-weight: 500;
      color: var(--hana-text-muted);
      background: var(--hana-surface);
      border-right: 1px solid var(--hana-border);
      user-select: none;
      flex-shrink: 0;
    }

    .hana-form-field__phone-select {
      padding: 8px 28px 8px 10px;
      font-size: var(--hana-body-size);
      font-weight: 500;
      font-family: var(--hana-font);
      color: var(--hana-text-muted);
      background: var(--hana-surface) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23757575' d='M1.41 0L6 4.58 10.59 0 12 1.41l-6 6-6-6z'/%3E%3C/svg%3E") no-repeat right 8px center;
      border: none;
      border-right: 1px solid var(--hana-border);
      flex-shrink: 0;
      cursor: pointer;
      outline: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      max-width: 120px;
    }

    .hana-form-field__phone-select:focus {
      color: var(--hana-text);
    }

    .hana-form-field__phone-select:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .hana-form-field__input--phone {
      border: none;
      border-radius: 0;
      padding: 8px 12px;
      flex: 1;
      min-width: 0;
    }

    .hana-form-field__input--phone:focus {
      border-color: transparent;
    }

    /* NRIC/Identification field */

    .hana-form-field__id-type-select {
      width: 100%;
      padding: 8px 36px 8px 12px;
      margin-bottom: 6px;
      border: 1px solid var(--hana-border);
      border-radius: 8px;
      font-family: var(--hana-font);
      font-size: var(--hana-body-size);
      color: var(--hana-text);
      background: var(--hana-bg) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23757575' d='M1.41 0L6 4.58 10.59 0 12 1.41l-6 6-6-6z'/%3E%3C/svg%3E") no-repeat right 12px center;
      outline: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      cursor: pointer;
      transition: border-color var(--hana-transition-speed) var(--hana-transition-easing);
    }

    .hana-form-field__id-type-select:focus {
      border-color: var(--hana-primary);
    }

    .hana-form-field__id-type-select:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      background: var(--hana-surface);
    }

    .hana-form-field__nric-wrapper {
      display: flex;
      align-items: center;
      gap: 0;
      border: 1px solid var(--hana-border);
      border-radius: 8px;
      background: var(--hana-bg);
      overflow: hidden;
      transition: border-color var(--hana-transition-speed) var(--hana-transition-easing);
    }

    .hana-form-field__nric-wrapper:focus-within {
      border-color: var(--hana-primary);
    }

    .hana-form-field__input--nric {
      border: none;
      border-radius: 0;
      padding: 8px 12px;
      flex: 1;
      min-width: 0;
    }

    .hana-form-field__input--nric:focus {
      border-color: transparent;
    }

    .hana-form-field__nric-toggle {
      width: 36px;
      height: 36px;
      border: none;
      background: transparent;
      color: var(--hana-text-muted);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      padding: 4px;
      outline: none;
      transition: color var(--hana-transition-speed) var(--hana-transition-easing);
    }

    .hana-form-field__nric-toggle:hover {
      color: var(--hana-primary-hover, var(--hana-primary));
    }

    .hana-form-field__nric-toggle:focus-visible {
      outline: 2px solid var(--hana-primary);
      outline-offset: -2px;
      border-radius: 4px;
    }

    .hana-form-field__nric-toggle svg {
      width: 18px;
      height: 18px;
      fill: currentColor;
    }

    .hana-form-field__nric-toggle:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    /* Select/Dropdown */

    .hana-form-field__select {
      width: 100%;
      padding: 8px 36px 8px 12px;
      border: 1px solid var(--hana-border);
      border-radius: 8px;
      font-family: var(--hana-font);
      font-size: var(--hana-body-size);
      color: var(--hana-text);
      background: var(--hana-bg) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23757575' d='M1.41 0L6 4.58 10.59 0 12 1.41l-6 6-6-6z'/%3E%3C/svg%3E") no-repeat right 12px center;
      outline: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      cursor: pointer;
      transition: border-color var(--hana-transition-speed) var(--hana-transition-easing);
    }

    .hana-form-field__select:focus {
      border-color: var(--hana-primary);
    }

    .hana-form-field__select--error {
      border-color: #D32F2F;
    }

    .hana-form-field__select:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      background: var(--hana-surface);
    }

    /* Textarea */

    .hana-form-field__textarea {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid var(--hana-border);
      border-radius: 8px;
      font-family: var(--hana-font);
      font-size: var(--hana-body-size);
      color: var(--hana-text);
      background: var(--hana-bg);
      outline: none;
      resize: vertical;
      min-height: 60px;
      transition: border-color var(--hana-transition-speed) var(--hana-transition-easing);
    }

    .hana-form-field__textarea:focus {
      border-color: var(--hana-primary);
    }

    .hana-form-field__textarea::placeholder {
      color: var(--hana-text-muted);
    }

    .hana-form-field__textarea--error {
      border-color: #D32F2F;
    }

    .hana-form-field__textarea:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      background: var(--hana-surface);
    }

    /* Date field */

    .hana-form-field__input--date {
      cursor: pointer;
    }

    /* Validation error */

    .hana-form-field__error {
      font-size: var(--hana-caption-size);
      color: #D32F2F;
      min-height: 0;
      line-height: 1.4;
    }

    .hana-form-field__error:empty {
      display: none;
    }

    /* Form actions */

    .hana-form__actions {
      display: flex;
      gap: 8px;
      margin-top: 16px;
    }

    .hana-form__btn {
      padding: 8px 20px;
      border-radius: var(--hana-cta-radius);
      font-family: var(--hana-font);
      font-size: var(--hana-body-size);
      font-weight: 600;
      cursor: pointer;
      border: none;
      outline: none;
      transition: background var(--hana-transition-speed) var(--hana-transition-easing),
                  opacity var(--hana-transition-speed) var(--hana-transition-easing);
    }

    .hana-form__btn:focus-visible {
      outline: 2px solid var(--hana-primary);
      outline-offset: 2px;
    }

    .hana-form__btn--submit {
      background: var(--hana-primary);
      color: #FFFFFF;
    }

    .hana-form__btn--submit:hover {
      background: var(--hana-primary-hover);
    }

    .hana-form__btn--cancel {
      background: transparent;
      color: var(--hana-text-muted);
      border: 1px solid var(--hana-border);
    }

    .hana-form__btn--cancel:hover {
      background: var(--hana-bg);
    }

    .hana-form__btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    /* Form status messages */

    .hana-form__status {
      font-size: var(--hana-caption-size);
      line-height: 1.5;
      padding: 8px 12px;
      border-radius: 8px;
      margin-top: 12px;
    }

    .hana-form__status--submitted {
      background: #E8F5E9;
      color: #2E7D32;
    }

    .hana-form__status--cancelled {
      background: #FFF3E0;
      color: #E65100;
    }

    /* ===== TEXT BUBBLES ===== */

    .hana-bubble {
      display: flex;
      flex-direction: column;
      max-width: 85%;
    }

    .hana-bubble--bot {
      align-self: flex-start;
    }

    .hana-bubble--user {
      align-self: flex-end;
    }

    .hana-bubble__content {
      padding: 10px 14px;
      border-radius: var(--hana-border-radius);
      font-size: var(--hana-body-size);
      line-height: 1.5;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }

    /* REQ-C-047 — specialist photo above a bot bubble's text. */
    .hana-bubble__photo {
      display: block;
      /* The bubble wrapper is a flex column; without an explicit
         align-self the photo is stretched to the full bubble width
         (align-items: stretch), which forces object-fit to crop the
         portrait vertically and cuts off the doctor's head. A non-stretch
         value keeps the image at its natural portrait proportions;
         center horizontally centers it above the bubble text. */
      align-self: center;
      max-width: 100%;
      width: auto;
      max-height: 180px;
      border-radius: var(--hana-border-radius);
      /* contain (never cover) so the whole photo is shown, never cropped. */
      object-fit: contain;
      margin-bottom: 6px;
    }

    .hana-bubble--bot .hana-bubble__content {
      background: var(--hana-bubble-bot-bg);
      color: var(--hana-text);
      border-bottom-left-radius: 4px;
    }

    .hana-bubble--user .hana-bubble__content {
      background: var(--hana-primary);
      color: #FFFFFF;
      border-bottom-right-radius: 4px;
    }

    /* Live human agent (Chatwoot takeover) — left-aligned like the bot
       but with a primary-color accent + attribution label so the patient
       can tell a human is replying (issue #497 follow-up #3). */
    .hana-bubble--agent {
      align-self: flex-start;
    }

    .hana-bubble--agent .hana-bubble__content {
      background: var(--hana-bubble-bot-bg);
      color: var(--hana-text);
      border-bottom-left-radius: 4px;
      border-left: 3px solid var(--hana-primary);
    }

    .hana-bubble__agent-label {
      font-size: var(--hana-caption-size);
      font-weight: 600;
      color: var(--hana-primary);
      margin: 0 0 2px 4px;
      letter-spacing: 0.02em;
    }

    .hana-bubble__content a {
      color: inherit;
      text-decoration: underline;
    }

    .hana-bubble--bot .hana-bubble__content a {
      color: var(--hana-primary-hover, var(--hana-primary));
    }

    .hana-bubble__content strong {
      font-weight: 600;
    }

    .hana-bubble__content em {
      font-style: italic;
    }

    .hana-bubble__content p {
      margin: 0 0 8px 0;
    }

    .hana-bubble__content p:last-child {
      margin-bottom: 0;
    }

    .hana-msg__list {
      margin: 4px 0;
      padding-left: 20px;
    }

    .hana-msg__list li {
      margin: 2px 0;
    }

    .hana-msg__phone {
      white-space: nowrap;
    }

    .hana-bubble__footer {
      display: flex;
      align-items: center;
      gap: 4px;
      margin-top: 4px;
      padding: 0 4px;
    }

    .hana-bubble--user .hana-bubble__footer {
      justify-content: flex-end;
    }

    .hana-bubble__time {
      font-size: var(--hana-caption-size);
      color: var(--hana-text-muted);
    }

    /* ===== MESSAGE STATUS ===== */

    .hana-status {
      display: inline-flex;
      align-items: center;
      width: 16px;
      height: 12px;
      color: var(--hana-text-muted);
    }

    .hana-status--read {
      color: var(--hana-primary);
    }

    .hana-status__icon {
      width: 16px;
      height: 12px;
    }

    .hana-status__sending {
      display: inline-block;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      border: 1.5px solid var(--hana-text-muted);
      border-top-color: transparent;
      animation: hana-spin 0.8s linear infinite;
    }

    @keyframes hana-spin {
      to { transform: rotate(360deg); }
    }

    /* ===== LIST SELECTION ===== */

    .hana-list {
      display: flex;
      flex-direction: column;
      max-width: 85%;
      align-self: flex-start;
    }

    .hana-list__header {
      font-size: var(--hana-body-size);
      color: var(--hana-text);
      padding: 0 0 8px 0;
      line-height: 1.5;
    }

    .hana-list__items {
      display: flex;
      flex-direction: column;
      gap: 4px;
      border: 1px solid var(--hana-border);
      border-radius: var(--hana-border-radius);
      overflow: hidden;
    }

    .hana-list__items--scrollable {
      max-height: 280px;
      overflow-y: auto;
    }

    .hana-list__item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 12px;
      border: none;
      background: var(--hana-bg);
      cursor: pointer;
      text-align: left;
      font-family: var(--hana-font);
      width: 100%;
      outline: none;
      transition: background var(--hana-transition-speed) var(--hana-transition-easing);
    }

    .hana-list__item:not(:last-child) {
      border-bottom: 1px solid var(--hana-border);
    }

    .hana-list__item:hover:not(:disabled) {
      background: var(--hana-surface);
    }

    .hana-list__item:focus-visible {
      outline: 2px solid var(--hana-primary);
      outline-offset: -2px;
    }

    .hana-list__item:disabled {
      cursor: default;
      opacity: 0.7;
    }

    .hana-list__item--selected {
      background: var(--hana-surface) !important;
      opacity: 1 !important;
    }

    .hana-list__icon {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      object-fit: cover;
      flex-shrink: 0;
    }

    .hana-list__text {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-width: 0;
    }

    .hana-list__title {
      font-size: var(--hana-body-size);
      font-weight: 500;
      color: var(--hana-text);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .hana-list__subtitle {
      font-size: var(--hana-caption-size);
      color: var(--hana-text-muted);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .hana-list__indicator {
      width: 20px;
      height: 20px;
      border: 2px solid var(--hana-border);
      border-radius: 50%;
      flex-shrink: 0;
      transition: border-color var(--hana-transition-speed) var(--hana-transition-easing),
                  background var(--hana-transition-speed) var(--hana-transition-easing);
    }

    .hana-list__item--selected .hana-list__indicator {
      border-color: var(--hana-primary);
      background: var(--hana-primary);
      box-shadow: inset 0 0 0 3px var(--hana-bg);
    }

    /* ===== PACKAGE CARDS ===== */

    .hana-cards {
      display: flex;
      flex-direction: column;
      max-width: 100%;
      align-self: flex-start;
    }

    .hana-cards__header {
      font-size: var(--hana-body-size);
      color: var(--hana-text);
      padding: 0 0 8px 0;
      line-height: 1.5;
    }

    .hana-cards__grid {
      display: grid;
      gap: 8px;
    }

    .hana-cards__grid--single {
      grid-template-columns: 1fr;
    }

    .hana-cards__grid--comparison {
      grid-template-columns: 1fr 1fr;
    }

    .hana-card {
      display: flex;
      flex-direction: column;
      border: 1px solid var(--hana-border);
      border-radius: var(--hana-border-radius);
      padding: 14px;
      background: var(--hana-bg);
      gap: 8px;
      position: relative;
    }

    /* REQ-D-037: full-width single-card recommendation treatment.
       Slightly larger padding + accent border so it reads as the
       picked option vs comparison cards. */
    .hana-card--recommended {
      padding: 18px;
      border-color: var(--hana-primary);
      border-width: 1.5px;
    }

    .hana-card__badge {
      position: absolute;
      top: -10px;
      right: 12px;
      background: var(--hana-primary);
      color: #FFFFFF;
      font-size: var(--hana-label-sm-size);
      font-weight: var(--hana-label-sm-weight);
      letter-spacing: 0.06em;
      padding: 4px 10px;
      border-radius: 12px;
      text-transform: uppercase;
      line-height: 1.2;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
    }

    .hana-card__name {
      font-size: var(--hana-body-size);
      font-weight: 600;
      color: var(--hana-text);
      line-height: 1.3;
    }

    .hana-card__details {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .hana-card__detail {
      font-size: var(--hana-caption-size);
      color: var(--hana-text-muted);
      line-height: 1.4;
    }

    .hana-card__price {
      font-size: var(--hana-heading-size);
      font-weight: var(--hana-heading-weight);
      color: var(--hana-text);
    }

    .hana-card__cta {
      padding: 8px 16px;
      border-radius: var(--hana-cta-radius);
      border: none;
      background: var(--hana-primary);
      color: #FFFFFF;
      font-family: var(--hana-font);
      font-size: var(--hana-body-size);
      font-weight: 500;
      cursor: pointer;
      outline: none;
      text-align: center;
      transition: background var(--hana-transition-speed) var(--hana-transition-easing);
    }

    .hana-card__cta:hover {
      background: var(--hana-primary-hover);
    }

    .hana-card__cta:focus-visible {
      outline: 2px solid var(--hana-primary);
      outline-offset: 2px;
    }

    .hana-card__cta-icon {
      display: inline-flex;
      align-items: center;
      margin-left: 4px;
      vertical-align: middle;
    }

    .hana-cards__help-choose {
      display: block;
      width: 100%;
      margin-top: 8px;
      padding: 8px;
      border: none;
      background: none;
      color: var(--hana-primary-hover, var(--hana-primary));
      font-family: var(--hana-font);
      font-size: var(--hana-body-size);
      cursor: pointer;
      text-align: center;
    }

    .hana-cards__help-choose:hover {
      text-decoration: underline;
    }

    .hana-cards__help-choose:focus-visible {
      outline: 2px solid var(--hana-primary);
      outline-offset: 2px;
    }

    /* ===== APPOINTMENT LIST ===== */

    .hana-appointment {
      display: flex;
      flex-direction: column;
      gap: 8px;
      width: 100%;
      padding: 0 4px;
    }

    .hana-appointment__header {
      font-size: var(--hana-body-size);
      font-weight: var(--hana-body-weight);
      color: var(--hana-text);
      margin-bottom: 4px;
    }

    .hana-appointment__section {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .hana-appointment__section-heading {
      font-size: var(--hana-caption-size);
      font-weight: 600;
      color: var(--hana-text-muted);
      text-transform: uppercase;
      letter-spacing: 0.5px;
      padding: 4px 0;
    }

    .hana-appointment__card {
      display: flex;
      flex-direction: column;
      gap: 2px;
      padding: 12px;
      border: 1px solid var(--hana-border);
      border-radius: var(--hana-border-radius);
      background: var(--hana-bg);
      cursor: pointer;
      text-align: left;
      font-family: var(--hana-font);
      width: 100%;
      transition: border-color var(--hana-transition-speed) var(--hana-transition-easing),
                  box-shadow var(--hana-transition-speed) var(--hana-transition-easing);
    }

    .hana-appointment__card:hover:not(:disabled):not(.hana-appointment__card--past) {
      border-color: var(--hana-primary);
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    }

    .hana-appointment__card:focus-visible {
      outline: 2px solid var(--hana-primary);
      outline-offset: 2px;
    }

    .hana-appointment__card--past {
      cursor: default;
      opacity: 0.65;
    }

    .hana-appointment__card--selected {
      border-color: var(--hana-primary);
      background: var(--hana-surface);
      box-shadow: 0 0 0 1px var(--hana-primary);
    }

    .hana-appointment__card:disabled {
      cursor: default;
      opacity: 0.5;
    }

    .hana-appointment__datetime {
      font-size: var(--hana-body-size);
      font-weight: 600;
      color: var(--hana-text);
    }

    .hana-appointment__doctor {
      font-size: var(--hana-body-size);
      font-weight: var(--hana-body-weight);
      color: var(--hana-text);
    }

    .hana-appointment__specialty {
      font-size: var(--hana-caption-size);
      color: var(--hana-text-muted);
    }

    .hana-appointment__location {
      font-size: var(--hana-caption-size);
      color: var(--hana-text-muted);
    }

    .hana-appointment__status {
      display: inline-block;
      font-size: 11px;
      font-weight: 500;
      padding: 2px 8px;
      border-radius: 10px;
      background: var(--hana-surface);
      color: var(--hana-text-muted);
      margin-top: 4px;
      align-self: flex-start;
    }

    .hana-appointment__counter {
      font-size: 11px;
      color: var(--hana-text-muted);
      margin-top: 2px;
    }

    .hana-appointment__counter--cancel {
      color: #B91C1C;
    }

    .hana-appointment__caveat {
      font-size: var(--hana-caption-size);
      font-style: italic;
      color: var(--hana-text-muted);
      padding: 8px 0 4px;
    }

    .hana-appointment__empty {
      font-size: var(--hana-body-size);
      color: var(--hana-text-muted);
      padding: 16px;
      text-align: center;
      border: 1px dashed var(--hana-border);
      border-radius: var(--hana-border-radius);
    }

    /* ===== TYPING INDICATOR ===== */

    .hana-typing {
      display: flex;
      align-self: flex-start;
      padding: 10px 14px;
      background: var(--hana-surface);
      border-radius: var(--hana-border-radius);
      border-bottom-left-radius: 4px;
    }

    .hana-typing--hidden {
      display: none;
    }

    .hana-typing__dots {
      display: flex;
      gap: 4px;
      align-items: center;
    }

    .hana-typing__dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--hana-text-muted);
      animation: hana-bounce 1.2s ease-in-out infinite;
    }

    @keyframes hana-bounce {
      0%, 60%, 100% {
        transform: translateY(0);
        opacity: 0.4;
      }
      30% {
        transform: translateY(-6px);
        opacity: 1;
      }
    }

    /* ===== SYSTEM MESSAGE ===== */

    .hana-system-msg {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
      padding: 4px 0;
    }

    .hana-system-msg__text {
      font-size: var(--hana-caption-size);
      color: var(--hana-text-muted);
      text-align: center;
      font-style: italic;
      padding: 4px 12px;
      background: var(--hana-surface);
      border-radius: 12px;
    }

    .hana-system-msg__time {
      font-size: 10px;
      color: var(--hana-text-muted);
    }

    /* ===== TIMESTAMP SEPARATOR ===== */

    .hana-timestamp {
      display: flex;
      justify-content: center;
      padding: 4px 0;
      font-size: var(--hana-caption-size);
      color: var(--hana-text-muted);
    }

    /* ===== REQ-D-046 SHELL VARIANTS ===== */

    /* Default panel mode keeps the legacy Wave-1 full-height behaviour.
       The size classes apply only when the embedder opted in (the JS
       layer only ever adds size classes alongside the mode class). */
    .hana-panel--mode-panel.hana-panel--size-compact {
      width: var(--hana-panel-width-compact);
      height: var(--hana-panel-height-compact);
      max-height: var(--hana-panel-height-compact);
      bottom: var(--hana-toggle-margin);
      right: var(--hana-toggle-margin);
      border-radius: 12px;
      transform: translateY(calc(100% + var(--hana-toggle-margin)));
    }
    .hana-panel--mode-panel.hana-panel--size-compact.hana-panel--left {
      left: var(--hana-toggle-margin);
      right: auto;
    }
    .hana-panel--mode-panel.hana-panel--size-compact.hana-panel--open {
      transform: translateY(0);
    }

    .hana-panel--mode-panel.hana-panel--size-expanded {
      width: var(--hana-panel-width-expanded);
      height: var(--hana-panel-height-expanded);
      max-height: var(--hana-panel-height-expanded);
      bottom: var(--hana-toggle-margin);
      right: var(--hana-toggle-margin);
      border-radius: 12px;
      transform: translateY(calc(100% + var(--hana-toggle-margin)));
    }
    .hana-panel--mode-panel.hana-panel--size-expanded.hana-panel--left {
      left: var(--hana-toggle-margin);
      right: auto;
    }
    .hana-panel--mode-panel.hana-panel--size-expanded.hana-panel--open {
      transform: translateY(0);
    }

    /* Modal mode — centred overlay launched by host CTA. */
    .hana-panel--mode-modal {
      width: min(var(--hana-modal-width), calc(100vw - 32px));
      height: min(var(--hana-modal-height), calc(100vh - 64px));
      max-height: calc(100vh - 64px);
      left: 50%;
      top: 50%;
      right: auto;
      bottom: auto;
      transform: translate(-50%, -50%) scale(0.96);
      opacity: 0;
      pointer-events: none;
      border-radius: 12px;
      transition: opacity var(--hana-size-transition),
                  transform var(--hana-size-transition);
    }
    .hana-panel--mode-modal.hana-panel--open {
      transform: translate(-50%, -50%) scale(1);
      opacity: 1;
      pointer-events: auto;
    }

    /* Modal backdrop overlay — sits behind the modal, dims the host. */
    .hana-modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, var(--hana-modal-backdrop-opacity));
      z-index: var(--hana-z-index);
      opacity: 0;
      pointer-events: none;
      transition: opacity var(--hana-size-transition);
    }
    .hana-modal-backdrop--open {
      opacity: 1;
      pointer-events: auto;
    }

    /* Hide the floating launcher when EITHER mode is wired to a host
       CTA (#857) — the host's own control is then the only launcher. */
    .hana-toggle--hidden {
      display: none !important;
    }

    /* REQ-D-046 header chrome — compact = 48px single line; expanded
       = 64px two-line with the "Concierge" subtitle. */
    .hana-header--compact {
      height: var(--hana-header-height-compact);
      min-height: var(--hana-header-height-compact);
    }
    .hana-header--compact .hana-header__subtitle {
      display: none;
    }
    .hana-header--expanded {
      height: var(--hana-header-height-expanded);
      min-height: var(--hana-header-height-expanded);
    }
    .hana-header__titles {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-width: 0;
      gap: 2px;
    }
    /* Modal mode — center the title block per the PDF modal mockup feel.
       Same markup, alignment-only override. */
    .hana-header--mode-modal .hana-header__titles {
      align-items: center;
      text-align: center;
    }
    .hana-header__subtitle {
      /* Subtitle on orange (#E8651A) header — rgba(255,255,255,0.9)
         resolves to luminance ~0.85 vs #E8651A's ~0.21 → contrast
         ratio ~5.8:1, comfortably passing WCAG AA (4.5:1). The opaque
         white-with-alpha rule survives dark-mode shells where
         --hana-primary-hover would resolve to an even darker stop. */
      color: rgba(255, 255, 255, 0.9);
      font-size: var(--hana-caption-size);
      font-weight: var(--hana-caption-weight);
      line-height: 1;
      letter-spacing: 0.02em;
    }

    /* REQ-D-046 bot avatar — translucent white disc with inset speech
       icon. 24px in compact (matches 48px single-line header), 32px in
       expanded (matches 64px two-line header). Sizes are CSS-driven so
       toggle between sizes never re-creates the DOM. */
    .hana-header__avatar {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.18);
      color: #ffffff;
      flex-shrink: 0;
      margin-right: 4px;
    }
    .hana-header__avatar svg {
      width: 18px;
      height: 18px;
      display: block;
    }
    .hana-header--compact .hana-header__avatar {
      width: 24px;
      height: 24px;
    }
    .hana-header--compact .hana-header__avatar svg {
      width: 14px;
      height: 14px;
    }
    .hana-header__toggle {
      width: 32px;
      height: 32px;
      border: none;
      background: transparent;
      color: #ffffff;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      flex-shrink: 0;
      padding: 0;
      outline: none;
      transition: background var(--hana-transition-speed)
        var(--hana-transition-easing);
    }
    .hana-header__toggle:hover {
      background: rgba(255, 255, 255, 0.15);
    }
    .hana-header__toggle:focus-visible {
      outline: 2px solid #ffffff;
      outline-offset: -2px;
    }
    .hana-header__toggle-icon svg {
      width: 16px;
      height: 16px;
      display: block;
      transition: transform var(--hana-size-transition);
    }
    /* Compact state rotates the chevron-stack so the up-arrow flips
       upward — purely cosmetic; aria-expanded is the real signal. */
    .hana-header--compact .hana-header__toggle-icon svg {
      transform: rotate(0deg);
    }
    .hana-header--expanded .hana-header__toggle-icon svg {
      transform: rotate(180deg);
    }
    .hana-header__toggle--hidden {
      display: none !important;
    }

    /* REQ-D-046 mobile bottom-sheet — applies to panel mode below 480px.
       100dvh keeps the fullscreen state keyboard-aware on iOS Safari. */
    @media (max-width: 480px) {
      .hana-panel--mode-panel.hana-panel--size-compact {
        width: 100vw;
        height: 40vh;
        max-height: 40vh;
        bottom: 0;
        right: 0;
        left: 0;
        border-radius: 12px 12px 0 0;
        transform: translateY(100%);
      }
      .hana-panel--mode-panel.hana-panel--size-compact.hana-panel--open {
        transform: translateY(0);
      }
      .hana-panel--mode-panel.hana-panel--size-expanded {
        width: 100vw;
        height: 90vh;
        max-height: 90vh;
        bottom: 0;
        right: 0;
        left: 0;
        border-radius: 12px 12px 0 0;
        transform: translateY(100%);
      }
      .hana-panel--mode-panel.hana-panel--size-expanded.hana-panel--open {
        transform: translateY(0);
      }
      .hana-panel--mode-panel.hana-panel--size-fullscreen {
        width: 100vw;
        height: 100dvh;
        max-height: 100dvh;
        bottom: 0;
        right: 0;
        left: 0;
        border-radius: 0;
        transform: translateY(100%);
      }
      .hana-panel--mode-panel.hana-panel--size-fullscreen.hana-panel--open {
        transform: translateY(0);
      }
      .hana-panel--mode-modal {
        width: 100vw;
        height: 100dvh;
        max-height: 100dvh;
        left: 0;
        top: 0;
        right: auto;
        bottom: auto;
        border-radius: 0;
        transform: translateY(100%);
      }
      .hana-panel--mode-modal.hana-panel--open {
        transform: translateY(0);
      }
    }

    /* REQ-D-046 size transition — gated on prefers-reduced-motion so
       motion-sensitive users get instant size flips. */
    @media (prefers-reduced-motion: no-preference) {
      .hana-panel {
        transition:
          width var(--hana-size-transition),
          height var(--hana-size-transition),
          transform var(--hana-transition-speed) var(--hana-transition-easing);
      }
    }

    /* ===== MOBILE OVERRIDES ===== */

    :host(.hana-mobile) .hana-panel {
      width: 100vw;
      height: 100vh;
      max-height: 100vh;
      border-radius: 0;
      box-shadow: none;
    }

    :host(.hana-mobile) .hana-toggle {
      bottom: 16px;
      right: 16px;
    }

    :host(.hana-mobile) .hana-toggle--left {
      right: auto;
      left: 16px;
    }

    /* ===== ACCESSIBILITY ===== */

    @media (prefers-reduced-motion: reduce) {
      :host {
        --hana-transition-speed: 0ms;
      }
      /* REQ-D-046: explicit zero transitions for motion-sensitive users.
         Without these, the panel slide-in + size-transition rules in the
         (no-preference) block above are simply absent in reduce mode —
         correct behaviour but easy to mistake for accidental omission.
         Setting transition: none here makes the intent loud. */
      .hana-panel,
      .hana-backdrop {
        transition: none;
      }
    }

    /* ===== SCROLLBAR ===== */

    .hana-messages::-webkit-scrollbar {
      width: 6px;
    }

    .hana-messages::-webkit-scrollbar-track {
      background: transparent;
    }

    .hana-messages::-webkit-scrollbar-thumb {
      background: var(--hana-border);
      border-radius: 3px;
    }

    .hana-messages::-webkit-scrollbar-thumb:hover {
      background: var(--hana-text-muted);
    }
  `;
}
